export const Badge = ({ certification, certified, loading }) => {
    const baseColor = certified ? (certification.color || '#4CAF50') : '#9E9E9E';

    // Color utility functions
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[ 1 ], 16),
            g: parseInt(result[ 2 ], 16),
            b: parseInt(result[ 3 ], 16)
        } : null;
    };

    const rgbToHex = (r, g, b) => {
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    };

    const lightenColor = (hex, percent) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return hex;

        const lighten = (c) => Math.min(255, Math.floor(c + (255 - c) * (percent / 100)));
        return rgbToHex(lighten(rgb.r), lighten(rgb.g), lighten(rgb.b));
    };

    const darkenColor = (hex, percent) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return hex;

        const darken = (c) => Math.max(0, Math.floor(c * (1 - percent / 100)));
        return rgbToHex(darken(rgb.r), darken(rgb.g), darken(rgb.b));
    };

    const desaturateColor = (hex, percent) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return hex;

        const gray = Math.floor((rgb.r + rgb.g + rgb.b) / 3);
        const desaturate = (c) => Math.floor(c + (gray - c) * (percent / 100));
        return rgbToHex(desaturate(rgb.r), desaturate(rgb.g), desaturate(rgb.b));
    };

    // Generate themed colors
    const borderColor = baseColor;
    const backgroundColor = desaturateColor(baseColor, 60); // Muted background
    const bannerColor = darkenColor(baseColor, 30); // Darker banner
    const bottomLeftColor = lightenColor(baseColor, 40); // Lighter left half
    const bottomRightColor = lightenColor(baseColor, 80); // Brighter right half

    // Generate unique ids for SVG gradients to prevent conflicts
    const uniqueId = `badge-${ certification.id }-${ Math.random().toString(36).substring(2, 9) }`;
    const badgeGradientId = `badgeGradient-${ uniqueId }`;
    const bottomGradientId = `bottomGradient-${ uniqueId }`;

    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 1024 1024"
            xmlns="http://www.w3.org/2000/svg"
            style={ { opacity: loading ? 0.5 : 1 } }
        >
            <defs>
                <linearGradient id={ badgeGradientId } x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={ { stopColor: backgroundColor, stopOpacity: 0.6 } } />
                    <stop offset="100%" style={ { stopColor: backgroundColor, stopOpacity: 0.4 } } />
                </linearGradient>

                <linearGradient id={ bottomGradientId } x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={ { stopColor: bottomLeftColor, stopOpacity: 0.8 } } />
                    <stop offset="50%" style={ { stopColor: bottomLeftColor, stopOpacity: 0.4 } } />
                    <stop offset="50%" style={ { stopColor: bottomRightColor, stopOpacity: 0.8 } } />
                    <stop offset="100%" style={ { stopColor: bottomRightColor, stopOpacity: 0.4 } } />
                </linearGradient>

                {/* Clip path matching the main badge shape so we can paint the bottom half
                    with a simple rectangle (avoids geometry misalignment and seams) */}
                <clipPath id={`mainClip-${ uniqueId }`}>
                    <path
                        d="M 792.09513,678.98986 523.50389,834.06109 a 45.827177,45.827177 2.5219885e-7 0 1 -45.82718,0 L 209.08547,678.98986 a 45.827177,45.827177 60 0 1 -22.91358,-39.6875 l 0,-310.14244 a 45.827177,45.827177 120 0 1 22.91359,-39.6875 L 477.67671,134.40119 a 45.827177,45.827177 2.521989e-7 0 1 45.82718,0 l 268.59124,155.07123 a 45.827177,45.827177 60 0 1 22.91359,39.6875 l 0,310.14244 a 45.827177,45.827177 120 0 1 -22.91359,39.6875 z"
                        transform="matrix(1.3669622,0,0,1.3669622,-172.28799,-149.92565)"
                    />
                </clipPath>
            </defs>

            {/* Main badge shape - shield/star-like */ }
            <path
                d="M 792.09513,678.98986 523.50389,834.06109 a 45.827177,45.827177 2.5219885e-7 0 1 -45.82718,0 L 209.08547,678.98986 a 45.827177,45.827177 60 0 1 -22.91358,-39.6875 l 0,-310.14244 a 45.827177,45.827177 120 0 1 22.91359,-39.6875 L 477.67671,134.40119 a 45.827177,45.827177 2.521989e-7 0 1 45.82718,0 l 268.59124,155.07123 a 45.827177,45.827177 60 0 1 22.91359,39.6875 l 0,310.14244 a 45.827177,45.827177 120 0 1 -22.91359,39.6875 z"
                transform="matrix(1.3669622,0,0,1.3669622,-172.28799,-149.92565)"
                fill={ `url(#${ badgeGradientId })` }
                strokeWidth="2"
            />

            {/* Bottom half coloring - rendered as a rectangle clipped to the main shape
                This prevents misalignment artifacts when the badge is scaled */}
            <g clipPath={ `url(#mainClip-${ uniqueId })` }>
                <rect x="0" y="512" width="1024" height="512" fill={ `url(#${ bottomGradientId })` } opacity="0.9" />
            </g>

            {/* Border */ }
            <path
                d="M 792.09513,678.98986 523.50389,834.06109 a 45.827177,45.827177 2.5219885e-7 0 1 -45.82718,0 L 209.08547,678.98986 a 45.827177,45.827177 60 0 1 -22.91358,-39.6875 l 0,-310.14244 a 45.827177,45.827177 120 0 1 22.91359,-39.6875 L 477.67671,134.40119 a 45.827177,45.827177 2.521989e-7 0 1 45.82718,0 l 268.59124,155.07123 a 45.827177,45.827177 60 0 1 22.91359,39.6875 l 0,310.14244 a 45.827177,45.827177 120 0 1 -22.91359,39.6875 z"
                transform="matrix(1.3669622,0,0,1.3669622,-172.28799,-149.92565)"
                fill="none"
                stroke={ borderColor }
                strokeWidth="20"
            />

            {/* Center emblem */ }
            <circle
                cx="512"
                cy="284.32455"
                r="150"
                fill="#ffffff"
                strokeWidth="2"
            />

            {/* Center cross/star */ }
            <path
                d="m 511.99964,141.20565 a 143.11905,143.11905 0 0 0 -143.1189,143.1189 143.11905,143.11905 0 0 0 143.1189,143.11892 143.11905,143.11905 0 0 0 143.11962,-143.11892 143.11905,143.11905 0 0 0 -143.11962,-143.1189 z m -40.93924,20.29971 h 81.87921 v 81.87923 h 81.87922 v 81.8792 h -81.87922 v 81.87923 H 471.0604 v -81.87923 h -81.87923 v -81.8792 h 81.87923 z"
                fill="#a82020ff"
            />

            {/* Banner background for badge text */ }
            <rect
                x="50"
                y="500"
                width="925"
                height="250"
                fill={ bannerColor }
                rx="5"
            />

            {/* Certification name */ }
            <text
                x="512"
                y="639.5791"
                textAnchor="middle"
                fontSize="104"
                fontWeight="bold"
                fontFamily="Arial, sans-serif"
                fill="#FFFFFF"
            >
                { certification.badge?.toUpperCase() || certification.name?.toUpperCase() }
            </text>

            {/* Expiry date */ }
            <text
                x="509.34778"
                y="849.52032"
                textAnchor="middle"
                fontSize="28"
                fontWeight="bold"
                fontFamily="Arial, sans-serif"
                fill="#000000"
            >
                { certification.expiry }
            </text>
        </svg>
    );
};